import type { SmriqJourneyError } from '../hooks/useSmriqJourney';

export interface LastCompletedServiceFieldErrors {
    service_option_id?: string;
    completed_date?: string;
    completed_mileage?: string;
    step?: string;
}

const PREFIX = 'last_completed_service';
const OPTION_KEYS = [PREFIX, `${PREFIX}.service_option_id`, `${PREFIX}.mode`] as const;

const firstMessage = (error: SmriqJourneyError, key: string): string | undefined =>
    error.fieldErrors?.[key]?.[0];

const START_MILEAGE_KEYS = ['plan_start_mileage', `${PREFIX}.plan_start_mileage`] as const;

const isStartMileageKey = (key: string | undefined): boolean =>
    !!key && (START_MILEAGE_KEYS as readonly string[]).includes(key);

const isPanelKey = (key: string): boolean =>
    key === PREFIX || key.startsWith(`${PREFIX}.`) || isStartMileageKey(key);

export const isLastCompletedServiceError = (error: SmriqJourneyError | null): boolean => {
    if (!error) return false;
    if (error.param && isPanelKey(error.param)) return true;
    return Object.keys(error.fieldErrors ?? {}).some(isPanelKey);
};

/** The plan-controls banner text, or undefined when the mounted panel shows every part of it. */
export const planControlsErrorMessage = (
    error: SmriqJourneyError,
    panelMounted: boolean,
): string | undefined => {
    if (!panelMounted || !isLastCompletedServiceError(error)) return error.message;
    const other = Object.entries(error.fieldErrors ?? {}).find(([key]) => !isPanelKey(key));
    if (other?.[1][0]) return other[1][0];
    return error.param && !isPanelKey(error.param) ? error.message : undefined;
};

/** `autoMatch` routes a bare-key rejection to the step-level message, since an Auto match has no
 *  service choice for it to attach to. */
export const lastCompletedServiceFieldErrors = (
    error: SmriqJourneyError | null,
    autoMatch = false,
): LastCompletedServiceFieldErrors => {
    if (!error) return {};
    const result: LastCompletedServiceFieldErrors = {};

    const optionMessage = OPTION_KEYS.map((key) => firstMessage(error, key)).find(Boolean);
    const date = firstMessage(error, `${PREFIX}.completed_date`);
    const startMileage = START_MILEAGE_KEYS.map((key) => firstMessage(error, key)).find(Boolean);
    const mileage = firstMessage(error, `${PREFIX}.completed_mileage`) ?? startMileage;

    if (optionMessage) {
        const bare = firstMessage(error, PREFIX) === optionMessage;
        if (autoMatch && bare) result.step = optionMessage;
        else result.service_option_id = optionMessage;
    }
    if (date) result.completed_date = date;
    if (mileage) result.completed_mileage = mileage;

    if (Object.keys(result).length === 0 && error.param === PREFIX) {
        if (autoMatch) result.step = error.message;
        else result.service_option_id = error.message;
    } else if (isStartMileageKey(error.param)) {
        result.completed_mileage ??= error.message;
    } else if (error.param === `${PREFIX}.completed_date`) {
        result.completed_date ??= error.message;
    } else if (error.param === `${PREFIX}.completed_mileage`) {
        result.completed_mileage ??= error.message;
    } else if (error.param === `${PREFIX}.service_option_id` || error.param === `${PREFIX}.mode`) {
        result.service_option_id ??= error.message;
    }
    return result;
};
